import { characterCards as fallbackCharacterCards } from "../game/characterCards";
import { GAME_MODES } from "../game/modes";
import { infinityCards as fallbackInfinityCards } from "../game/infinityCards";
import { shuffle } from "../game/deckEngine";
import {
  resolveStoryBattleCharacterRole,
  STORY_BATTLE_ROLE_SOURCE,
} from "./storyHumanCharacterCards";
import { resolveStoryCompanionAura } from "./storyCompanions";
import { buildStoryForestEncounterBattle } from "./storyForestEncounters";
import { buildStorySideQuestEncounterBattle } from "./storySideQuests";
import {
  STORY_CAVE_ENCOUNTER_SET_ID,
  buildStoryCaveEncounterBattle,
  isStoryCaveEncounterId,
} from "./storyCaveEncounters";
import {
  buildStoryBattleTokenReward,
  buildUsedStoryInfinityCards,
} from "./storyModeBattleRewardUtils";
import { buildStoryBattleXpAward } from "./storyModeBattleXPUtils";

export const STORY_BATTLE_RESULT_KEYS = Object.freeze({
  STORY_SIDE_WIN: "story_side_win",
  OPPONENT_SIDE_WIN: "opponent_side_win",
  STORY_SIDE_TIMEOUT_WIN: "story_side_timeout_win",
  OPPONENT_SIDE_TIMEOUT_WIN: "opponent_side_timeout_win",
  QUIT: "quit",
  DISCONNECT: "disconnect",
  DRAW: "draw",
  COMPLETED_UNKNOWN: "completed_unknown",
});

export const STORY_BATTLE_STATUS = Object.freeze({
  NOT_STARTED: "not_started",
  ACTIVE: "active",
  COMPLETED: "completed",
});

export const STORY_BATTLE_LAUNCH_MODE = Object.freeze({
  DIRECT_MATCH: "direct_match",
});

const STORY_BATTLE_SESSION_KEY = "storyBattleHandoff";
const TOSHI_CHARACTER_ID = "QW8eUlAKTTcLwX7nyKmY";
const TRAINING_DECK_CARD_IDS = Object.freeze([
  "zLZ7TJphndSOaOC5n3xK",
  "jdly8VShytJCnWzbsYgg",
  "BItmGNgIv4o969Jkd9sR",
  "JWB2oZbpSbR00K0mVAeV",
]);

const TRAINING_BATTLE_RETURN_TARGET = Object.freeze({
  mapId: "voidCity",
  building: "arena",
  spawnId: "arena-training-return",
  x: 324,
  y: 302,
});

const TRAINING_COMPLETION_POLICY = Object.freeze({
  defaultResultKey: STORY_BATTLE_RESULT_KEYS.COMPLETED_UNKNOWN,
  onResults: Object.freeze({
    [STORY_BATTLE_RESULT_KEYS.STORY_SIDE_WIN]: Object.freeze({
      storyOutcome: "success",
      advance: true,
    }),
    [STORY_BATTLE_RESULT_KEYS.OPPONENT_SIDE_WIN]: Object.freeze({
      storyOutcome: "lesson",
      advance: true,
    }),
    [STORY_BATTLE_RESULT_KEYS.STORY_SIDE_TIMEOUT_WIN]: Object.freeze({
      storyOutcome: "success",
      advance: true,
    }),
    [STORY_BATTLE_RESULT_KEYS.OPPONENT_SIDE_TIMEOUT_WIN]: Object.freeze({
      storyOutcome: "lesson",
      advance: true,
    }),
    [STORY_BATTLE_RESULT_KEYS.QUIT]: Object.freeze({
      storyOutcome: "lesson",
      advance: true,
    }),
    [STORY_BATTLE_RESULT_KEYS.DISCONNECT]: Object.freeze({
      storyOutcome: "lesson",
      advance: true,
    }),
    [STORY_BATTLE_RESULT_KEYS.DRAW]: Object.freeze({
      storyOutcome: "lesson",
      advance: true,
    }),
    [STORY_BATTLE_RESULT_KEYS.COMPLETED_UNKNOWN]: Object.freeze({
      storyOutcome: "lesson",
      advance: true,
    }),
  }),
});

export const cloneStoryBattleValue = (value) =>
  JSON.parse(JSON.stringify(value));

const STORY_BATTLE_ENCOUNTERS = Object.freeze({
  "arena-training-1": Object.freeze({
    encounterId: "arena-training-1",
    launchMode: STORY_BATTLE_LAUNCH_MODE.DIRECT_MATCH,
    mode: GAME_MODES.TEAMS,
    storySide: Object.freeze({
      sideId: "red",
      leaderRole: Object.freeze({
        source: STORY_BATTLE_ROLE_SOURCE.STORY_HUMAN,
        characterId: "zay",
      }),
      teammateRoles: Object.freeze([
        Object.freeze({
          source: STORY_BATTLE_ROLE_SOURCE.CATALOG,
          characterId: TOSHI_CHARACTER_ID,
          alias: "toshi",
        }),
      ]),
    }),
    opponentSide: Object.freeze({
      sideId: "blue",
      opponentRoles: Object.freeze([
        Object.freeze({
          source: STORY_BATTLE_ROLE_SOURCE.STORY_HUMAN,
          characterId: "infinity-orb",
          alias: "Infinity Orb A",
          statOverrides: Object.freeze({
            health: 5,
            attack: 2,
            defense: 2,
            accuracy: 5,
          }),
        }),
      ]),
    }),
    roomConfig: Object.freeze({
      amountOfPlayers: 3,
      botFill: false,
      public: false,
      ranked: false,
      timedTurns: true,
      turnTime: 30,
      mode: GAME_MODES.TEAMS,
      teamCount: 2,
      teamOrder: ["red", "blue"],
    }),
    completionPolicy: TRAINING_COMPLETION_POLICY,
    returnTarget: TRAINING_BATTLE_RETURN_TARGET,
    resumePolicy: Object.freeze({
      allowResumeActiveMatch: true,
      restartIfRoomMissing: true,
    }),
  }),
  "arena-training-2": Object.freeze({
    encounterId: "arena-training-2",
    launchMode: STORY_BATTLE_LAUNCH_MODE.DIRECT_MATCH,
    mode: GAME_MODES.TEAMS,
    storySide: Object.freeze({
      sideId: "red",
      leaderRole: Object.freeze({
        source: STORY_BATTLE_ROLE_SOURCE.STORY_HUMAN,
        characterId: "zay",
      }),
      teammateRoles: Object.freeze([
        Object.freeze({
          source: STORY_BATTLE_ROLE_SOURCE.CATALOG,
          characterId: TOSHI_CHARACTER_ID,
          alias: "toshi",
        }),
      ]),
    }),
    opponentSide: Object.freeze({
      sideId: "blue",
      opponentRoles: Object.freeze([
        Object.freeze({
          source: STORY_BATTLE_ROLE_SOURCE.STORY_HUMAN,
          characterId: "infinity-orb",
          alias: "Infinity Orb A",
          statOverrides: Object.freeze({
            health: 5,
            attack: 2,
            defense: 3,
            accuracy: 5,
          }),
        }),
        Object.freeze({
          source: STORY_BATTLE_ROLE_SOURCE.STORY_HUMAN,
          characterId: "infinity-orb",
          alias: "Infinity Orb B",
          statOverrides: Object.freeze({
            health: 6,
            attack: 3,
            defense: 2,
            accuracy: 4,
          }),
        }),
      ]),
    }),
    roomConfig: Object.freeze({
      amountOfPlayers: 4,
      botFill: false,
      public: false,
      ranked: false,
      timedTurns: true,
      turnTime: 30,
      mode: GAME_MODES.TEAMS,
      teamCount: 2,
      teamOrder: ["red", "blue"],
    }),
    completionPolicy: TRAINING_COMPLETION_POLICY,
    returnTarget: TRAINING_BATTLE_RETURN_TARGET,
    resumePolicy: Object.freeze({
      allowResumeActiveMatch: true,
      restartIfRoomMissing: true,
    }),
  }),
  "arena-training-3": Object.freeze({
    encounterId: "arena-training-3",
    launchMode: STORY_BATTLE_LAUNCH_MODE.DIRECT_MATCH,
    mode: GAME_MODES.TEAMS,
    storySide: Object.freeze({
      sideId: "red",
      leaderRole: Object.freeze({
        source: STORY_BATTLE_ROLE_SOURCE.STORY_HUMAN,
        characterId: "zay",
      }),
      teammateRoles: Object.freeze([
        Object.freeze({
          source: STORY_BATTLE_ROLE_SOURCE.CATALOG,
          characterId: TOSHI_CHARACTER_ID,
          alias: "toshi",
        }),
      ]),
    }),
    opponentSide: Object.freeze({
      sideId: "blue",
      opponentRoles: Object.freeze([
        Object.freeze({
          source: STORY_BATTLE_ROLE_SOURCE.STORY_HUMAN,
          characterId: "infinity-orb",
          alias: "Infinity Orb A",
          statOverrides: Object.freeze({
            health: 5,
            attack: 2,
            defense: 3,
            accuracy: 6,
          }),
        }),
        Object.freeze({
          source: STORY_BATTLE_ROLE_SOURCE.STORY_HUMAN,
          characterId: "infinity-orb",
          alias: "Infinity Orb B",
          statOverrides: Object.freeze({
            health: 6,
            attack: 3,
            defense: 2,
            accuracy: 5,
          }),
        }),
        Object.freeze({
          source: STORY_BATTLE_ROLE_SOURCE.STORY_HUMAN,
          characterId: "infinity-orb",
          alias: "Infinity Orb C",
          statOverrides: Object.freeze({
            health: 4,
            attack: 4,
            defense: 2,
            accuracy: 5,
          }),
        }),
      ]),
    }),
    roomConfig: Object.freeze({
      amountOfPlayers: 5,
      botFill: false,
      public: false,
      ranked: false,
      timedTurns: true,
      turnTime: 30,
      mode: GAME_MODES.TEAMS,
      teamCount: 2,
      teamOrder: ["red", "blue"],
    }),
    completionPolicy: TRAINING_COMPLETION_POLICY,
    returnTarget: TRAINING_BATTLE_RETURN_TARGET,
    resumePolicy: Object.freeze({
      allowResumeActiveMatch: true,
      restartIfRoomMissing: true,
    }),
  }),
});

const normalizeBattleState = (battleState = {}) => ({
  roomId: battleState?.roomId || null,
  accessCode: battleState?.accessCode || null,
  status: battleState?.status || STORY_BATTLE_STATUS.NOT_STARTED,
  resultKey: battleState?.resultKey || null,
  launchedAt: battleState?.launchedAt || null,
  completedAt: battleState?.completedAt || null,
});

const toArray = (value) => (Array.isArray(value) ? value : []);

const mergeCatalogCharacters = (catalogCharacters = []) => {
  const byId = new Map();
  [...toArray(fallbackCharacterCards), ...toArray(catalogCharacters)].forEach((character) => {
    if (!character?.id) return;
    byId.set(String(character.id), character);
  });
  return [...byId.values()];
};

const mergeInfinityCards = (catalogInfinityCards = []) => {
  const byId = new Map();
  [...toArray(fallbackInfinityCards), ...toArray(catalogInfinityCards)].forEach((card) => {
    if (!card?.id) return;
    byId.set(String(card.id), card);
  });
  return [...byId.values()];
};

const findCatalogInfinityCard = (item = {}, catalogInfinityCards = []) => {
  const normalized = String(item?.sourceId || item?.id || item?.name || "")
    .trim()
    .toLowerCase();
  if (!normalized) return null;

  return (
    mergeInfinityCards(catalogInfinityCards).find((card) => {
      const candidateIds = [card?.id, card?.sourceId, card?.name]
        .filter(Boolean)
        .map((value) => String(value).trim().toLowerCase());
      return candidateIds.includes(normalized);
    }) || null
  );
};

const buildShuffledStoryBattleCards = (cards = [], maxCards = null) => {
  const limitedCards =
    Number.isFinite(maxCards) && maxCards > 0 ? cards.slice(0, maxCards) : cards;
  const shuffledCards = shuffle(limitedCards).map((card) => cloneStoryBattleValue(card));

  return {
    hand: shuffledCards.slice(0, 5),
    deck: shuffledCards.slice(5),
  };
};

export const buildStoryInventoryDeckCards = ({
  storyInventory = [],
  catalogInfinityCards = [],
} = {}) => {
  const cards = [];

  toArray(storyInventory)
    .filter((item) => item?.kind === "infinityCard")
    .forEach((item) => {
      const card = findCatalogInfinityCard(item, catalogInfinityCards);
      if (!card) return;

      const quantity = Math.max(0, Math.floor(Number(item?.quantity || 0)));
      for (let copyIndex = 0; copyIndex < quantity; copyIndex += 1) {
        cards.push(cloneStoryBattleValue(card));
      }
    });

  return buildShuffledStoryBattleCards(cards);
};

export const buildStoryTrainingDeckCards = (catalogInfinityCards = []) => {
  const cardLookup = new Map(
    mergeInfinityCards(catalogInfinityCards).map((card) => [String(card.id), card]),
  );

  const deck = [];
  TRAINING_DECK_CARD_IDS.forEach((cardId) => {
    const card = cardLookup.get(cardId);
    if (!card) return;
    for (let copyIndex = 0; copyIndex < 10; copyIndex += 1) {
      deck.push(cloneStoryBattleValue(card));
    }
  });

  if (deck.length < 40) {
    throw new Error("Story training deck could not be assembled from the card catalog.");
  }

  return buildShuffledStoryBattleCards(deck, 40);
};

const buildRoomPlayer = ({
  id,
  name,
  role,
  team,
  isBot,
  selectedCardSlap,
  selectedTradeAnimation,
  avatar = null,
  orbAvatar = null,
  nfteam = null,
  level = 1,
  xp = 0,
  partyId = null,
  record = null,
  sticker = null,
  catalogCharacters = [],
  catalogInfinityCards = [],
  storyInventory = [],
  storyCharacterBoosts = {},
  useStoryInventoryDeck = false,
  companionAura = null,
}) => {
  const baseCharacter = resolveStoryBattleCharacterRole(role, {
    catalogCharacters: mergeCatalogCharacters(catalogCharacters),
    storyCharacterBoosts,
  });

  if (!baseCharacter) {
    throw new Error(`Unable to resolve story battle character for role ${role?.characterId || "unknown"}.`);
  }

  const character =
    companionAura && (Number(companionAura.attackDelta || 0) || Number(companionAura.accuracyDelta || 0))
      ? {
          ...baseCharacter,
          attack: Math.max(
            0,
            Number(baseCharacter.attack || 0) + Number(companionAura.attackDelta || 0),
          ),
          accuracy: Math.max(
            0,
            Number(baseCharacter.accuracy || 0) + Number(companionAura.accuracyDelta || 0),
          ),
          buffs: {
            ...(baseCharacter.buffs || {}),
            attack: [
              ...((baseCharacter.buffs || {}).attack || []),
              {
                amt: Number(companionAura.attackDelta || 0),
                duration: 999,
                name: `${companionAura.companion?.name || "Companion"} Aura`,
                source: "storyCompanion",
              },
            ].filter((entry) => Number(entry?.amt || 0) !== 0),
            accuracy: [
              ...((baseCharacter.buffs || {}).accuracy || []),
              {
                amt: Number(companionAura.accuracyDelta || 0),
                duration: 999,
                name: `${companionAura.companion?.name || "Companion"} Aura`,
                source: "storyCompanion",
              },
            ].filter((entry) => Number(entry?.amt || 0) !== 0),
          },
          storyCompanionAura: {
            verdict: companionAura.verdict,
            companionId: companionAura.companion?.id || null,
          },
        }
      : baseCharacter;

  const { hand, deck } = useStoryInventoryDeck
    ? buildStoryInventoryDeckCards({ storyInventory, catalogInfinityCards })
    : buildStoryTrainingDeckCards(catalogInfinityCards);

  return {
    id,
    isBot,
    name,
    username: name,
    ready: true,
    team,
    avatar,
    orbAvatar,
    nfteam,
    level,
    xp,
    partyId,
    record,
    sticker,
    selectedCardSlap,
    selectedTradeAnimation,
    character,
    hand,
    deck,
  };
};

export const buildStoryBattleRoomPlayers = ({
  battle,
  userData,
  effectiveUserData,
  storyInventory = [],
  storyCharacterBoosts = {},
  catalogCharacters = [],
  catalogInfinityCards = [],
  activeStoryCompanionId = null,
}) => {
  const storySide = battle?.storySide || {};
  const opponentSide = battle?.opponentSide || {};
  const mergedCatalogCharacters = mergeCatalogCharacters(catalogCharacters);
  const enemyElements = (opponentSide.opponentRoles || [])
    .map((role) =>
      resolveStoryBattleCharacterRole(role, {
        catalogCharacters: mergedCatalogCharacters,
        storyCharacterBoosts,
      }),
    )
    .filter(Boolean)
    .map((character) => String(character?.element || "").trim().toLowerCase())
    .filter(Boolean);
  const companionAura = resolveStoryCompanionAura({
    activeStoryCompanionId,
    enemyElements,
  });
  const playerName =
    effectiveUserData?.username ||
    userData?.username ||
    userData?.name ||
    "Player";
  const localPlayer = buildRoomPlayer({
    id: userData.id,
    name: playerName,
    role: storySide.leaderRole,
    team: storySide.sideId || "red",
    isBot: false,
    selectedCardSlap: Number(userData?.selectedCardSlap || 1),
    selectedTradeAnimation: Number(
      userData?.selectedTradeAnimation ||
        userData?.selectedTradAnimation ||
        1,
    ),
    avatar: effectiveUserData?.avatar || null,
    orbAvatar: effectiveUserData?.orbAvatar || null,
    nfteam: effectiveUserData?.nfteam || userData?.nfteam || null,
    level: Number(effectiveUserData?.level || userData?.level || 1),
    xp: Number(userData?.xp || 0),
    partyId: userData?.partyId || null,
    record: userData?.record || null,
    sticker: userData?.sticker || null,
    storyInventory,
    storyCharacterBoosts,
    useStoryInventoryDeck: true,
    companionAura,
    catalogCharacters: mergedCatalogCharacters,
    catalogInfinityCards,
  });

  const teammates = (storySide.teammateRoles || []).map((role, index) =>
    buildRoomPlayer({
      id: `story_bot_${battle.encounterId}_ally_${index + 1}`,
      name: role.alias ? String(role.alias).toUpperCase() : `Ally ${index + 1}`,
      role,
      team: storySide.sideId || "red",
      isBot: true,
      selectedCardSlap: 1,
      selectedTradeAnimation: 1,
      storyCharacterBoosts,
      companionAura,
      catalogCharacters: mergedCatalogCharacters,
      catalogInfinityCards,
    }),
  );

  const opponents = (opponentSide.opponentRoles || []).map((role, index) => {
    const fallbackName =
      role.alias || role.characterId || `Opponent ${index + 1}`;
    return buildRoomPlayer({
      id: `story_bot_${battle.encounterId}_enemy_${index + 1}`,
      name: String(fallbackName).replace(/(^\w)|(-\w)/g, (match) =>
        match.replace("-", "").toUpperCase(),
      ),
      role,
      team: opponentSide.sideId || "blue",
      isBot: true,
      selectedCardSlap: 1,
      selectedTradeAnimation: 1,
      storyCharacterBoosts,
      catalogCharacters: mergedCatalogCharacters,
      catalogInfinityCards,
    });
  });

  return [localPlayer, ...teammates, ...opponents];
};

export const getStoryBattleEncounter = (encounterId) =>
  STORY_BATTLE_ENCOUNTERS[String(encounterId || "")] || null;

export const buildStoryWorldEncounterBattle = ({
  missionId = "",
  encounterMode = "",
  encounterSetId = "",
  encounterId = "",
  storyState = null,
  world = null,
} = {}) => {
  const normalizedEncounterMode = String(encounterMode || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z]/g, "");
  if (normalizedEncounterMode !== "roamingcreatureclear") return null;

  if (!missionId || !encounterSetId || !encounterId) return null;

  if (
    encounterSetId === STORY_CAVE_ENCOUNTER_SET_ID ||
    isStoryCaveEncounterId(encounterId)
  ) {
    return buildStoryCaveEncounterBattle({
      encounterId,
      storyState,
      world,
    });
  }

  const sideQuestBattle = buildStorySideQuestEncounterBattle({
    encounterSetId,
    encounterId,
    storyState,
  });
  if (sideQuestBattle) {
    return sideQuestBattle;
  }

  return buildStoryForestEncounterBattle({
    encounterId,
    storyState,
    world,
  });
};

export const buildInitialStoryBattleState = (battleState = {}) =>
  normalizeBattleState(battleState);

export const isStoryBattleCompleted = (battleState = {}) =>
  normalizeBattleState(battleState).status === STORY_BATTLE_STATUS.COMPLETED;

export const buildStoryBattlePayload = ({
  mission,
  world,
  existingBattleState = {},
  existingBattleEncounterId = null,
}) => {
  const encounter = getStoryBattleEncounter(mission?.battleEncounterId);
  if (!encounter) return null;
  const shouldCarryExistingBattleState =
    !existingBattleEncounterId ||
    String(existingBattleEncounterId) === String(encounter.encounterId);

  return {
    battle: {
      launchMode: encounter.launchMode,
      encounterId: encounter.encounterId,
      mode: encounter.mode,
      storySide: cloneStoryBattleValue(encounter.storySide),
      opponentSide: cloneStoryBattleValue(encounter.opponentSide),
      roomConfig: cloneStoryBattleValue(encounter.roomConfig),
      completionPolicy: cloneStoryBattleValue(encounter.completionPolicy),
      returnTarget: {
        ...cloneStoryBattleValue(encounter.returnTarget),
        ...(world || {}),
      },
      resumePolicy: cloneStoryBattleValue(encounter.resumePolicy),
    },
    battleState: normalizeBattleState(
      shouldCarryExistingBattleState ? existingBattleState : {},
    ),
  };
};

export const getStoryBattleOutcomeDecision = (battle = {}, resultKey) => {
  const policy = battle?.completionPolicy?.onResults || {};
  const resolvedResultKey =
    resultKey || battle?.completionPolicy?.defaultResultKey || STORY_BATTLE_RESULT_KEYS.COMPLETED_UNKNOWN;

  return (
    policy[resolvedResultKey] ||
    policy[battle?.completionPolicy?.defaultResultKey] || {
      storyOutcome: "lesson",
      advance: true,
    }
  );
};

export const normalizeStoryBattleResultKey = ({
  battle = {},
  matchData = {},
  userId,
}) => {
  if (!matchData) return STORY_BATTLE_RESULT_KEYS.COMPLETED_UNKNOWN;

  const timedOut = matchData?.timeoutResolution?.reason === "end_match_timer";
  const players = Array.isArray(matchData?.players) ? matchData.players : [];
  const winnerId = matchData?.winner?.id || null;
  const playerRow = players.find((player) => player?.id === userId) || null;
  const winnerRow = players.find((player) => player?.id === winnerId) || null;
  const storySideId = battle?.storySide?.sideId || "red";
  const winnerTeam = matchData?.winner?.team || winnerRow?.team || null;

  if (winnerTeam) {
    const storyWon = winnerTeam === storySideId;
    if (storyWon) {
      return timedOut
        ? STORY_BATTLE_RESULT_KEYS.STORY_SIDE_TIMEOUT_WIN
        : STORY_BATTLE_RESULT_KEYS.STORY_SIDE_WIN;
    }
    return timedOut
      ? STORY_BATTLE_RESULT_KEYS.OPPONENT_SIDE_TIMEOUT_WIN
      : STORY_BATTLE_RESULT_KEYS.OPPONENT_SIDE_WIN;
  }

  if (winnerId) {
    const storyWon = winnerId === userId || (!winnerRow?.isBot && playerRow);
    if (storyWon) {
      return timedOut
        ? STORY_BATTLE_RESULT_KEYS.STORY_SIDE_TIMEOUT_WIN
        : STORY_BATTLE_RESULT_KEYS.STORY_SIDE_WIN;
    }
    return timedOut
      ? STORY_BATTLE_RESULT_KEYS.OPPONENT_SIDE_TIMEOUT_WIN
      : STORY_BATTLE_RESULT_KEYS.OPPONENT_SIDE_WIN;
  }

  if (matchData?.status === "finished") {
    return STORY_BATTLE_RESULT_KEYS.COMPLETED_UNKNOWN;
  }

  return null;
};

export const isStoryBattleRoom = (room = {}) => Boolean(room?.storyBattleMeta?.missionId);

export const buildStoryBattleHandoffPayload = ({
  roomId,
  roomData = {},
  matchData = {},
  userId,
  resultKeyOverride = null,
  catalogInfinityCards = [],
}) => {
  if (!isStoryBattleRoom(roomData)) return null;

  const battle = roomData?.storyBattleMeta?.battle || null;
  const resultKey =
    resultKeyOverride ||
    normalizeStoryBattleResultKey({ battle, matchData, userId }) ||
    STORY_BATTLE_RESULT_KEYS.COMPLETED_UNKNOWN;
  const xpAward = buildStoryBattleXpAward({
    roomId,
    missionId: roomData.storyBattleMeta.missionId,
    encounterId: roomData.storyBattleMeta.encounterId,
    battle,
    matchData,
    resultKey,
  });
  const storyTokenReward = buildStoryBattleTokenReward({
    roomId,
    missionId: roomData.storyBattleMeta.missionId,
    encounterId: roomData.storyBattleMeta.encounterId,
    battle,
    matchData,
    resultKey,
  });
  const usedStoryInfinityCards = buildUsedStoryInfinityCards({
    matchData,
    userId,
    catalogInfinityCards,
  });

  return {
    roomId,
    missionId: roomData.storyBattleMeta.missionId,
    encounterId: roomData.storyBattleMeta.encounterId,
    resultKey,
    xpAward,
    storyTokenReward,
    usedStoryInfinityCards,
    returnTarget: roomData.storyBattleMeta.returnTarget || null,
    resolvedAt: Date.now(),
  };
};

export const saveStoryBattleHandoff = (payload) => {
  if (!payload) return;
  try {
    sessionStorage.setItem(STORY_BATTLE_SESSION_KEY, JSON.stringify(payload));
  } catch {}
};

export const peekStoryBattleHandoff = (missionId) => {
  try {
    const raw = sessionStorage.getItem(STORY_BATTLE_SESSION_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (missionId && parsed?.missionId !== missionId) {
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
};

export const consumeStoryBattleHandoff = (missionId) => {
  const parsed = peekStoryBattleHandoff(missionId);
  if (!parsed) return null;

  try {
    sessionStorage.removeItem(STORY_BATTLE_SESSION_KEY);
  } catch {}

  return parsed;
};
